import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../../../components/core/Button';
import { useWizard } from '../../../context/WizardContext';
import { Badge } from '../../../components/core/Badge';
import { generateExamQuestions } from '../../../api/question.api';
import { getTemplates } from '../../../database/templates';

export function Step8Generate() {
  const { state, goPrev, onComplete } = useWizard();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGenerate() {
    if (
      !state.selectedSubject ||
      !state.selectedPaper ||
      !state.selectedExamType ||
      !state.selectedVersion ||
      !state.selectedClass ||
      !state.selectedDepartment ||
      state.selectedChapters.length === 0
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const matchedTemplates = getTemplates(state.selectedSubject.id, state.selectedExamType);
      const template = matchedTemplates[0];
      const templateParts = template?.parts;

      if (!templateParts || templateParts.length === 0) {
        setError('No template found for the selected subject and exam type.');
        setIsLoading(false);
        return;
      }

      const examPaper = await generateExamQuestions({
        version: state.selectedVersion,
        classLevel: state.selectedClass,
        department: state.selectedDepartment,
        subject: state.selectedSubject.name,
        subjectCode: state.subjectCode || state.selectedSubject.code,
        paper: state.selectedPaper.name,
        institutionName: state.institutionName,
        chapters: state.selectedChapters,
        examType: state.selectedExamType,
        numberOfQuestions: state.numberOfQuestions,
        duration: state.duration,
        totalMarks: state.totalMarks,
        templateParts,
        stimulusInstruction: template.stimulusInstruction,
        banglaStimlusInstruction: template.banglaStimlusInstruction,
        difficulty: state.difficulty,
      });

      if (onComplete) {
        onComplete(examPaper);
      } else {
        navigate('/questions', { state: { examPaper } });
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to generate questions. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="space-y-8">
      <div className="text-center space-y-2">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-white">Ready to Generate!</h2>
        <p className="text-slate-400 max-w-md mx-auto">
          Your AI-powered exam questions will be generated based on your selected settings.
        </p>
      </div>

      {/* Summary */}
      <div className="rounded-2xl border border-slate-700 bg-slate-700/50 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Summary</h3>
        <div className="flex flex-wrap gap-2">
          {state.selectedSubject && <Badge variant="default">{state.selectedSubject.name}</Badge>}
          {state.selectedPaper && <Badge variant="secondary">{state.selectedPaper.name}</Badge>}
          {state.selectedChapters.map((ch) => (
            <Badge key={ch.id} variant="outline">
              {ch.name}
            </Badge>
          ))}
          {state.selectedExamType && (
            <Badge variant="success">
              {state.selectedExamType === 'written' ? 'Written' : 'MCQ'}
            </Badge>
          )}
          {state.numberOfQuestions > 0 && (
            <Badge variant="outline">{state.numberOfQuestions} questions</Badge>
          )}
          {state.duration > 0 && <Badge variant="outline">{state.duration} min</Badge>}
        </div>
      </div>

      <div className="flex flex-col items-center gap-4">
        {error && (
          <div className="w-full rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}
        <Button
          size="lg"
          onClick={handleGenerate}
          disabled={isLoading}
          className="w-full sm:w-auto px-10"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Generating with AI…
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              Generate Questions
            </>
          )}
        </Button>
        <p className="text-xs text-slate-500">
          AI will generate curriculum-aligned questions for Bangladesh{' '}
          {state.selectedClass === 10 ? 'SSC' : 'HSC'} board exam.
        </p>
      </div>

      <div className="flex justify-start">
        <Button variant="outline" onClick={goPrev} disabled={isLoading}>
          ← Previous
        </Button>
      </div>
    </div>
  );
}
